<script setup lang="ts" generic="T extends FormChoiceValue">
// Types
import type { FormChoiceEntry, FormChoiceValue } from "../types/formselect";

const { fullWidthOptions, options = [] } = defineProps<{
  fullWidthOptions?: boolean;
  options: FormChoiceEntry<T>[];
}>();

const field = useFormControl<T[]>();

if (!field)
  throw new Error(
    "[FormCheckbox]: field instance not available. Did you wrap this component inside a FormControl?",
  );

const checkedValues = defineModel<T[]>({
  set(value) {
    field.setValue(value || undefined, field.validateOnValueUpdate);

    return value;
  },
  default: [],
});

if (field.value.value) {
  checkedValues.value = field.value.value;
}

defineExpose({
  field,
});
</script>

<template>
  <div
    :class="[
      'form-checkbox',
      {
        'form-checkbox--full-width-options': fullWidthOptions,
      },
    ]"
  >
    <label
      v-for="(option, key) in options"
      :key="key"
      :class="[
        'form-checkbox__label',
        {
          'form-checkbox__label--disabled': option.disabled,
        },
      ]"
      :for="`${option.value}`"
    >
      <input
        v-model="checkedValues"
        :id="`${option.value}`"
        class="form-checkbox__native-element u-visually-hidden"
        :disabled="option.disabled"
        type="checkbox"
        :value="option.value"
      />
      <div class="form-checkbox__ui-element">
        <div class="form-checkbox__ui-element-backdrop"></div>
        <div class="form-checkbox__ui-element-border"></div>
        <svg
          class="form-checkbox__ui-checked-element"
          width="24px"
          height="24px"
          viewBox="0 0 24 24"
        >
          <path
            d="M11.0746 12.4433L14.8118 8.70694C15.2023 8.31649 15.8354 8.31653 16.2259 8.70702L16.2929 8.77399C16.6834 9.16451 16.6834 9.79768 16.2929 10.1882L11.7817 14.6994C11.3912 15.0899 10.758 15.0899 10.3675 14.6994L7.70736 12.0393C7.31673 11.6487 7.31685 11.0153 7.70761 10.6248L7.77503 10.5575C8.1656 10.1672 8.79857 10.1673 9.189 10.5577L11.0746 12.4433Z"
          ></path>
        </svg>
      </div>
      {{ option.label }}
    </label>
  </div>
</template>

<style scoped lang="scss">
/* stylelint-disable selector-class-pattern */
/* stylelint-disable no-descending-specificity */

/* TODO: fix all the specificity issues */
.form-checkbox {
  order: 1;

  &__label {
    display: inline-flex;
    gap: var(--form-checkbox-label-gap, 0.5rem);
    align-items: center;
    margin-block: var(--form-checkbox-offset, 0.5rem);

    & + & {
      margin-inline: var(--form-checkbox-label-gap, 0.5rem);
    }
  }

  &__label:hover &__ui-element-backdrop,
  &__native-element:focus ~ &__ui-element &__ui-element-backdrop {
    opacity: 1;
  }

  &__ui-element {
    position: relative;
    width: 1em;
    height: 1em;
    font-size: var(--form-checkbox-ui-element-size, 20px);
  }

  &__ui-element-border {
    position: relative;
    display: block;
    box-sizing: border-box;
    width: 1em;
    height: 1em;
    border: var(--form-radio-ui-element-border-size, 0.12em) solid
      var(--form-radio-ui-element-border-color, #333);
    border-radius: var(--form-checkbox-ui-element-border-radius, 0.2em);
  }

  &__ui-element-backdrop {
    position: absolute;
    inset: -3px;
    background-color: var(--form-checkbox-ui-element-backdrop-color, #f3f3f3);
    border-radius: var(--form-checkbox-ui-element-border-radius, 0.2em);
    opacity: 0;
    transition-timing-function: ease-in-out;
    transition-duration: 0.2s;
    transition-property: opacity, transform;
  }

  &__ui-checked-element {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0.8em;
    height: 0.8em;
    font-size: var(--form-checkbox-ui-checked-element-size, 1.875em);
    transform: translate(-50%, -50%) scale(0.8);
    transform-origin: center;
    opacity: 0;
    transition-timing-function: ease-in-out;
    transition-duration: 0.2s;
    transition-property: opacity, fill, transform;
    fill: var(--form-checkbox-ui-checked-element-placeholder-color, #e6e6e6);
  }

  &__native-element:checked ~ &__ui-element &__ui-checked-element,
  &__native-element:focus ~ &__ui-element &__ui-checked-element,
  &__label:hover &__ui-element &__ui-checked-element {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }

  &__label:hover input:not(:checked) ~ &__ui-element &__ui-checked-element {
    fill: var(--form-checkbox-ui-checked-element-placeholder-color, #e6e6e6);
  }

  &__native-element:checked ~ &__ui-element &__ui-checked-element,
  &__label:hover input:checked ~ &__ui-element &__ui-checked-element {
    fill: var(--form-checkbox-ui-checked-element-color, #03d386);
  }

  /* Variants */
  &--full-width-options &__label {
    display: flex;
  }

  &--full-width-options &__label + &__label {
    margin-inline: 0;
  }

  /* States */
  &__label--disabled {
    --form-checkbox-ui-element-border-color: var(
      --form-element-disabled-color,
      #a0a0a0
    );

    color: var(--form-element-disabled-color, #a0a0a0);
    cursor: not-allowed;
  }

  &__label--disabled &__ui-element-backdrop,
  &__label--disabled &__ui-checked-element {
    display: none;
  }
}
</style>
